import React, { useRef, forwardRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DatePickerWithInput from "./DatePickerWithInput";

const FilterPopup = forwardRef(
  (
    {
      isOpen,
      onClose,
      fromDate,
      setFromDate,
      toDate,
      setToDate,
      locations,
      selectedLocations,
      toggleLocationSelection,
      getDisplayText,
      onReset,
      onApply,
    },
    ref,
  ) => {
    const locationDropdownRef = useRef(null);
    const [isLocationDropdownOpen, setIsLocationDropdownOpen] =
      React.useState(false);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          locationDropdownRef.current &&
          !locationDropdownRef.current.contains(event.target)
        ) {
          setIsLocationDropdownOpen(false);
        }
      };

      if (isLocationDropdownOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.body.style.overflow = "auto";
      };
    }, [isLocationDropdownOpen]);

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4"
          >
            <motion.div
              ref={ref}
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
            >
              <div className="p-6 pb-0">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-QBold text-gray-800">
                    Filter Options
                  </h2>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto px-6 flex-1">
                <div className="space-y-4 pb-4">
                  <DatePickerWithInput
                    label="From Date"
                    value={fromDate}
                    onChange={setFromDate}
                  />

                  <DatePickerWithInput
                    label="To Date"
                    value={toDate}
                    onChange={setToDate}
                  />

                  <div className="relative" ref={locationDropdownRef}>
                    <label className="block text-sm font-QSemiBold text-gray-700 mb-1">
                      Locations
                    </label>
                    <button
                      onClick={() =>
                        setIsLocationDropdownOpen(!isLocationDropdownOpen)
                      }
                      className="w-full flex justify-between font-QSemiBold items-center border border-gray-200 rounded-xl px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="truncate">
                        {getDisplayText(selectedLocations)}
                      </span>
                      <i
                        className={`ri-arrow-down-s-line text-gray-500 transition-transform ${isLocationDropdownOpen ? "rotate-180" : ""}`}
                      ></i>
                    </button>

                    <AnimatePresence>
                      {isLocationDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute z-20 w-full bottom-[50px] bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                        >
                          <div className="max-h-48 overflow-y-auto custom-scrollbar">
                            {locations.map((location, index) => (
                              <div
                                key={location.locationID || index}
                                onClick={() =>
                                  toggleLocationSelection(location)
                                }
                                className={`px-4 py-3 font-QMedium cursor-pointer hover:bg-indigo-50 flex items-center ${
                                  selectedLocations.includes(location)
                                    ? "bg-indigo-100 text-indigo-700"
                                    : "text-gray-700"
                                }`}
                              >
                                <span className="truncate">
                                  {location.locationName}
                                </span>
                                {selectedLocations.includes(location) && (
                                  <i className="ri-check-line ml-auto text-indigo-600"></i>
                                )}
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              <div className="p-6 pt-4 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={onReset}
                    className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 font-QSemiBold hover:bg-gray-50 transition"
                  >
                    Reset
                  </button>
                  <button
                    onClick={onApply}
                    className="px-4 py-2 bg-indigo-600 rounded-xl text-white font-QSemiBold hover:bg-indigo-700 transition"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  },
);

export default FilterPopup;
